import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { VariableDetails } from '../../../../shared/ontology/model/variable-details';
import { Formula } from '../../../../shared/ontology/model/formula';
import { ModeView } from '../../../../shared/ontology/model/mode-view.enum';
import { FormulaVariable } from '../../../../shared/ontology/model/formula-variable';
import { FormulaService } from '../../../../shared/ontology/service/formula.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '../../../../shared/alert/alert.service';
import { Router } from '@angular/router';
import { FormulaEditorComponent } from '../formula-editor/formula-editor.component';
import { HELP_MANAGE_ONTOLOGIES_FORMULAS } from '../../../../app.constants';

@Component({
    selector: 'jhi-formula-dialog',
    templateUrl: './formula-dialog.component.html'
})
export class FormulaDialogComponent implements OnInit {
    protected readonly Number = Number;
    protected readonly ModeView = ModeView;
    protected readonly HELP_MANAGE_ONTOLOGIES_FORMULAS = HELP_MANAGE_ONTOLOGIES_FORMULAS;
    protected readonly DEFINITION_TEXT_LIMIT = 255;

    @ViewChild('f', { static: true }) form!: NgForm;
    @ViewChild(FormulaEditorComponent) formulaEditor!: FormulaEditorComponent;

    isLoading = false;
    variable!: VariableDetails;
    formula!: Formula;
    modeView!: ModeView;

    showWarningEditableMessage = false;
    filteredGroupedVariables: { [key: string]: VariableDetails[] } = {};

    private inputByNames = new Map<string, FormulaVariable>();

    constructor(
        public activeModal: NgbActiveModal,
        private alertService: AlertService,
        private router: Router,
        private formulaService: FormulaService
    ) {
    }

    ngOnInit(): void {
        this.formula = this.buildFormula(this.variable);
        this.modeView = this.variable?.formula?.formulaId ? ModeView.EDIT : ModeView.CREATE;

        this.showWarningEditableMessage =
            !!this.variable?.formula?.formulaId &&
            !!this.variable?.metadata &&
            this.variable.metadata.deletable === false;
    }

    private buildFormula(variable: VariableDetails): Formula {
        if (variable?.formula?.formulaId) {
            const formula: Formula = structuredClone(variable.formula);
            (formula.inputs ?? []).forEach(inputVariable => {
                const name = (inputVariable?.name ?? '').trim();
                if (name) {
                    this.inputByNames.set(name, inputVariable);
                }
            });
            return formula;
        }
        return this.initilizeFormula(variable);
    }

    initilizeFormula(variable: VariableDetails): Formula {
        const target = new FormulaVariable();
        target.name = variable?.name;
        target.id = parseInt(variable?.id, 10);

        const formula: Formula = new Formula();
        formula.target = target;
        formula.inputs = [];
        formula.definition = '';
        formula.active = true;
        formula.name = '';
        formula.description = '';
        return formula;
    }

    public onDefinitionChanged(definition: string): void {
        this.formula.definition = definition ?? '';
        this.syncInputsFromDefinition();
    }

    onAddInputVariable(variable: VariableDetails): void {
        const name = (variable?.name ?? '').trim();
        if (!name) {
            return;
        }

        this.formulaEditor.focus();
        this.formulaEditor.insertToken(name);

        if (!this.formula.inputs) {
            this.formula.inputs = [];
        }

        const variableId = parseInt(variable.id, 10);
        const exists = this.formula.inputs.some(input => input.targetTermId === variableId);

        if (!exists) {
            const formulaVariable = new FormulaVariable();
            formulaVariable.targetTermId = variableId;
            formulaVariable.name = name;
            this.formula.inputs.push(formulaVariable);
            this.inputByNames.set(name, formulaVariable);
        }
    }

    /**
     * Ensure consistency: inputs must contain all variables referenced in the definition.
     * If the user deletes tokens or pastes text, this re-syncs.
     */
    private syncInputsFromDefinition(): void {
        const def = this.formula.definition ?? '';
        const variableNames = this.extractVariableNames(def);

        const keep = new Set(variableNames);

        const current = this.formula.inputs ?? [];
        const filtered = current.filter(i => keep.has((i?.name ?? '').trim()));

        this.formula.inputs = filtered;

        this.inputByNames.clear();
        filtered.forEach(i => {
            const name = (i?.name ?? '').trim();
            if (name) {
                this.inputByNames.set(name, i);
            }
        });
    }

    private extractVariableNames(definition: string): string[] {
        const regex = /\{\{([\w\r\s_]+)\}\}/g;
        const variableNames: string[] = [];
        let match: RegExpExecArray | null;

        while ((match = regex.exec(definition ?? '')) !== null) {
            const name = (match[1] ?? '').trim();
            if (name) {
                variableNames.push(name);
            }
        }

        return Array.from(new Set(variableNames));
    }

    save(): void {
        if (!this.form?.valid) {
            this.form?.control?.markAllAsTouched();
            return;
        }

        // Re-sync inputs before sending
        this.syncInputsFromDefinition();
        this.isLoading = true;

        const req$ = this.variable?.formula?.formulaId
            ? this.formulaService.update(this.formula)
            : this.formulaService.create(this.formula);

        req$.subscribe({
            next: () => {
                this.isLoading = false;
                this.modeView === ModeView.EDIT
                    ? this.alertService.success('ontology-manager.variable.formula.updated.success')
                    : this.alertService.success('ontology-manager.variable.formula.created.success');

                this.close();
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    discard(): void {
        this.close();
    }

    private close(): void {
        this.activeModal.dismiss('close');
        setTimeout(() => {
            this.router.navigate(
                ['/', { outlets: { popup: ['variables', this.variable.id, 'view'] } }],
                { queryParamsHandling: 'merge' }
            );
        });
    }
}
